import React, { useMemo, useState } from 'react';
import { AdminLayout } from 'apps/sigma2-ui/src/components/AdminLayout';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { useSimpleReadPattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadPattern';
import { useCallback } from 'react';
import { Organizations } from 'libs/end-to-end/src/types/organizations/organizations';
import { CircularProgress } from '@mui/material';
import { SimpleInput } from 'apps/sigma2-ui/src/components/ui/SimpleInput';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { SaveIndicator } from 'apps/sigma2-ui/src/components/SaveIndicator';
import { useSimpleWritePattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleWritePattern';
import { OrganizationGroupInsertion } from 'libs/end-to-end/src/group-entitlements';
import { OrganizationGroupSelection } from 'libs/end-to-end/src/group-entitlements';
import { BaseError } from 'libs/end-to-end/src/types/errors/base-error';
import { useNavigateSafe } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';
import { SelectBoxWithSearch } from 'apps/sigma2-ui/src/components/ui/SelectBoxWithSearch';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { getTranslatedContentAsString } from 'libs/end-to-end/src/translation-content';
import { convertTranslatedContentToReactNode } from 'apps/sigma2-ui/src/lib/hooks/useTranslation';
import { AdminBreadcrumbs } from 'apps/sigma2-ui/src/components/ui/AdminBreadcrumbs';

export const GroupNewPage = () => {
  const language = useLanguage();
  const navigate = useNavigateSafe();
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [groupName, setGroupName] = useState('');
  const [createError, setCreateError] = useState<BaseError | null>(null);

  const [isCreatingGroup, setIsCreatingGroup] = useState(false);

  const getUrlFn = useCallback(
    () => `${getApiBaseUrl()}/organizations?groupManagement=true`,
    [],
  );

  const readOrgsResult = useSimpleReadPattern({
    getUrlFn,
    canRead: true,
    responseSchema: Organizations,
  });

  const isLoading = readOrgsResult.status === 'loading';
  const isError = readOrgsResult.status === 'error';

  const organizations = useMemo(() => {
    if (readOrgsResult.status !== 'done') {
      return [];
    }

    return readOrgsResult.entity.organizations;
  }, [readOrgsResult]);

  const getGroupUrlFn = useCallback(() => `${getApiBaseUrl()}/groups`, []);

  const writePattern = useSimpleWritePattern({
    getUrlFn: getGroupUrlFn,
    canWrite: !isLoading,
    writeSchema: OrganizationGroupInsertion,
    responseSchema: OrganizationGroupSelection,
    method: 'POST',
  });

  const createGroupDisabled = useMemo(() => {
    return groupName.length === 0 || !organizationId || isCreatingGroup;
  }, [groupName.length, organizationId, isCreatingGroup]);

  const onCreateGroup = useCallback(() => {
    if (!organizationId || writePattern.status === 'saving') {
      return;
    }

    setIsCreatingGroup(true);

    writePattern.saveEntity(
      {
        name: groupName,
        organizationId,
      },
      () => {
        navigate(`/groups`);
      },
      (err) => {
        setIsCreatingGroup(false);
        setCreateError(err);
      },
    );
  }, [groupName, navigate, organizationId, writePattern]);

  return (
    <AdminLayout currentPage={'GroupNew'}>
      <AdminBreadcrumbs
        items={[
          { label: 'Home', to: '/admin' },
          { label: 'Groups', to: '/groups' },
          { label: 'New Group', to: '/groups/new' },
        ]}
      />
      {isLoading && (
        <CircularProgress
          className="text-secondary"
          color="inherit"
          size={96}
          thickness={8}
        />
      )}
      {isError && (
        <S2Typography
          variant={'headline-7-alt'}
          translatedContent={language.unknownError()}
        />
      )}
      {!isLoading && (
        <Spacing size={'md'} direction={'vertical'}>
          <div>
            <SimpleInput
              label={getTranslatedContentAsString(language.groupNameLabel())}
              value={groupName}
              onChange={setGroupName}
            />
          </div>

          <SelectBoxWithSearch
            type={'single'}
            setSelected={(newValue) => {
              setOrganizationId(newValue === 'none' ? null : newValue);
            }}
            selected={organizationId ?? 'none'}
            list={[
              { optionValue: 'none', optionLabel: 'None' },
              ...organizations.map((o) => {
                return {
                  optionValue: o.id,
                  optionLabel: o.name,
                };
              }),
            ]}
            label={'Select Organization'}
          />

          <Spacing size={'md'} direction={'horizontal'}>
            <Spacing size="sm" direction="horizontal">
              <Button
                behavior={{
                  type: 'href',
                  href: '/groups',
                }}
              >
                {convertTranslatedContentToReactNode(language.cancel())}
              </Button>

              <Button
                behavior={{
                  type: 'onClick',
                  onClick: onCreateGroup,
                }}
                disabled={createGroupDisabled}
              >
                {convertTranslatedContentToReactNode(
                  language.createGroupButton(),
                )}
              </Button>
            </Spacing>

            {createError && (
              <S2Typography
                variant={'headline-7-alt'}
                translatedContent={language.errorCreatingGroup({
                  baseError: createError,
                })}
              />
            )}
            <SaveIndicator status={writePattern.saveIndicatorStatus} />
          </Spacing>
        </Spacing>
      )}
    </AdminLayout>
  );
};
